import { Printer, Download } from "lucide-react";
import Modal from "../Modal";
import PhaseChip from "../PhaseChip";
import { uploadUrl } from "../../lib/uploads";
import { printElement } from "../../lib/printArea";
import useServerPdf from "../../lib/useServerPdf";
import { useBranding } from "../../context/TenantBrandingContext";

const FALLBACK_LOGO = "/brand/rogue-white-blue.png";

export default function TicketPrintModal({ ticket, customerName, onClose }) {
  const { branding } = useBranding();
  // [Iter 290zp.13t] PDFs render on white paper — prefer the light
  // variant. Falls back to logo_url for tenants without it set.
  const BRAND_LOGO = branding?.logo_url_light || branding?.logo_url || FALLBACK_LOGO;
  const BRAND_NAME = branding?.app_name || "Rogue";
  const shortId = ticket?.short_id || ticket?.id?.slice(0, 8) || "ticket";
  const { download: downloadPdf, busy: downloadingPdf } = useServerPdf({
    endpoint: ticket?.id ? `/tickets/${ticket.id}/pdf` : null,
    filename: `${BRAND_NAME}_Ticket_${shortId}.pdf`,
  });

  if (!ticket) return null;
  const handlePrint = () =>
    printElement("ticket-print-area", {
      title: `Ticket #${ticket.short_id || ticket.id?.slice(0, 8) || ""}`,
    });
  // [Iter 327] `uploadUrl()` normalises bare-id / path / stale full URL.
  const photoUrl = (p) => (p?.startsWith("data:") ? p : uploadUrl(p) || p);

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Support ticket · Document view"
        testId="ticket-print-modal"
        maxWidth="max-w-3xl"
      >
        <div className="space-y-3" data-testid="ticket-print-content">
          <div className="no-print flex justify-end gap-2 -mt-2">
            <button
              type="button"
              onClick={downloadPdf}
              disabled={downloadingPdf}
              className="hv-btn hv-btn-primary"
              data-testid="ticket-download-pdf-btn"
              title="Download a server-rendered PDF — always light mode, deterministic layout"
            >
              <Download className="w-4 h-4" />
              {downloadingPdf ? "Generating…" : "Download PDF"}
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="hv-btn hv-btn-ghost"
              data-testid="ticket-print-btn"
              title="Print in browser — always renders in light mode"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>

          <div
            id="ticket-print-area"
            className="pcard border border-white/10 rounded-md p-5 md:p-6 bg-[#0a0b0e]"
          >
            {/* Compact header */}
            <div className="flex items-start justify-between gap-3 pb-2 border-b pdivider border-white/10">
              <div className="flex items-center gap-3">
                <img src={BRAND_LOGO} alt={BRAND_NAME} className="h-10 md:h-12 w-auto object-contain" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] paccent text-[#1fb6c1] font-bold">
                    {BRAND_NAME} · Support Ticket
                  </div>
                  <div className="font-display text-lg md:text-xl font-bold ptext leading-tight">
                    {ticket.subject}
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display text-2xl md:text-3xl font-bold paccent text-[#1fb6c1] leading-none">
                  #{ticket.short_id || ticket.id?.slice(0, 8)}
                </div>
                {ticket.external_ref && (
                  <div className="text-[10px] font-mono pmeta text-[#dde3ec] mt-0.5">
                    {ticket.external_ref}
                  </div>
                )}
              </div>
            </div>

            {/* Meta grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 mt-3 text-sm avoid-break">
              <Meta label="Customer" value={customerName} />
              <Meta
                label="Status"
                valueEl={
                  <span className="pchip">
                    <PhaseChip value={ticket.status} />
                  </span>
                }
              />
              <Meta
                label="Logged at"
                value={ticket.created_at ? new Date(ticket.created_at).toLocaleString('en-GB') : "—"}
              />
              <Meta label="Priority" value={ticket.priority || "normal"} />
              {ticket.support_type && <Meta label="Type" value={ticket.support_type} />}
              {ticket.logged_by && <Meta label="Logged by" value={ticket.logged_by} />}
            </div>

            {ticket.description && (
              <Section label="Description">
                <p className="text-sm whitespace-pre-wrap leading-relaxed ptext text-[#dde3ec]">
                  {ticket.description}
                </p>
              </Section>
            )}

            {ticket.resolution_notes && (
              <Section label="Resolution">
                <div className="px-3 py-2 rounded border border-emerald-500/40 bg-emerald-500/10 ptext text-[#dde3ec] text-sm whitespace-pre-wrap leading-relaxed avoid-break">
                  {ticket.resolution_notes}
                </div>
              </Section>
            )}

            {!!(ticket.notes || []).length && (
              <Section label="Updates">
                <ul className="space-y-2">
                  {ticket.notes.map((n) => (
                    <li
                      key={n.id}
                      className="pcard border border-white/10 rounded p-2 text-xs avoid-break"
                    >
                      <div className="text-[10px] plabel text-[#1fb6c1] uppercase tracking-wider font-bold">
                        {n.by_user_name} · {new Date(n.created_at).toLocaleString('en-GB')}
                      </div>
                      <div className="mt-0.5 whitespace-pre-wrap ptext text-[#dde3ec]">{n.text}</div>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {!!(ticket.photos || []).length && (
              <Section label="Photos">
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {ticket.photos.map((p, i) => (
                    <img
                      key={i}
                      src={photoUrl(p)}
                      alt={`ticket-photo-${i}`}
                      className="rounded border border-white/10 max-h-40 w-full object-cover"
                    />
                  ))}
                </div>
              </Section>
            )}

            <div className="pt-3 mt-4 border-t pdivider border-white/10 text-center">
              <div className="text-[10px] uppercase tracking-[0.25em] pmeta text-[#dde3ec]">
                Generated {new Date().toLocaleString('en-GB')} ·{" "}
                <span className="paccent text-[#1fb6c1] font-bold">{BRAND_NAME}</span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

function Meta({ label, value, valueEl }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[10px] uppercase tracking-[0.18em] plabel text-[#1fb6c1] font-bold shrink-0 min-w-[78px]">
        {label}
      </span>
      {valueEl ? (
        <span className="ptext text-[#dde3ec]">{valueEl}</span>
      ) : (
        <span className="text-sm ptext text-[#dde3ec] break-words">{value || "—"}</span>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="mt-4">
      <div className="text-[10px] uppercase tracking-[0.2em] plabel text-[#1fb6c1] mb-2 font-bold">
        {label}
      </div>
      {children}
    </div>
  );
}
